import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { matchPath, useLocation } from 'react-router'
import { useEventSlug, useQuery } from 'hooks'
import { getNodes } from 'graphql/utils'
import {
    LibraryImagesQuery,
    LibraryImagesQueryVariables,
    OrganizationImagesQuery,
    OrganizationImagesQueryVariables
} from 'generated/graphql'
import routes from 'routes'
import { isAuthenticatedSelector, isOrganizationAdminSelector } from 'features/auth/authSlice'
import { eventOrganizationIdSelector } from 'features/events/eventSlice'
import { createImage, deleteImage } from './graphql/mutation'
import { adminOrganizationImagesQuery, libraryImagesQuery } from './graphql/query'
import BaseImageLibrary from './BaseImageLibrary'

interface Image {
    id: number
    file: string
}

type Props = {
    onSelectImage?: (imageUrl: string) => void
}

const ImageLibrary = ({ onSelectImage } : Props) => {
    const isAuthenticated = useSelector(isAuthenticatedSelector)
    const eventSlug = useEventSlug()
    const location = useLocation()
    const organizationPath = matchPath<{ organizationId: string }>(location.pathname, routes.organization)
    const eventOrganizationId = useSelector(eventOrganizationIdSelector) || ''
    // When organization admin is managing the library (at /organization/:id path),
    // we use organizationId from path's param
    // In the other cases, we use organizationId from event data.
    const organizationId = organizationPath?.params?.organizationId || `${eventOrganizationId}`

    const { data: organizationData } = useQuery<
        OrganizationImagesQuery, OrganizationImagesQueryVariables
    >(adminOrganizationImagesQuery, { organizationId }, null, !isAuthenticated || !organizationId)
    const organizationImages = useMemo(() => (
        getNodes(organizationData?.organization?.images)
    ), [organizationData])

    const { data: libraryData } = useQuery<
        LibraryImagesQuery, LibraryImagesQueryVariables
    >(libraryImagesQuery, { eventSlug }, null, !isAuthenticated || !eventSlug)
    const libraryImages = useMemo(() => (
        getNodes(libraryData?.event.organization?.images)
    ), [libraryData])

    const isOrganizationAdmin = useSelector(isOrganizationAdminSelector(organizationId))

    const images = useMemo<Image[]>(() => {
        // set initial organization images
        // When organization admin is managing the library, we will use images from organization query
        // In the other cases, we will get images from event -> organization data.
        if (organizationImages.length) {
            return organizationImages
        }

        if (libraryImages.length) {
            return libraryImages
        }

        return []
    }, [organizationImages, libraryImages])

    const getImageUrl = useCallback((image: Image) => image.file, [])

    const handleSelectImage = useCallback(async (image) => {
        onSelectImage(getImageUrl(image))
    }, [getImageUrl, onSelectImage])

    const handleUploadImage = useCallback(async (file) => {
        const { data: res } = await createImage(organizationId, file)
        return res?.createImage
    }, [organizationId])

    const handleDeleteImage = useCallback(async (image) => {
        await deleteImage(image.id)
    }, [])

    return (
        <BaseImageLibrary
            images={images}
            getImageUrl={getImageUrl}
            canDelete={isOrganizationAdmin}
            canUpload={isOrganizationAdmin}
            onSelectImage={onSelectImage && handleSelectImage}
            onUploadImage={handleUploadImage}
            onDeleteImage={handleDeleteImage}
        />
    )
}

export default ImageLibrary
